import { useContext, useEffect, useMemo, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import { buildClasses } from "../utils/buildClasses";
import { RyeNoResults } from "../rye/RyeNoResults";
import { RyeMainContentHeader } from "../rye/RyeMainContentHeader";
import { RyeMainContentSpacer } from "../rye/RyeMainContentSpacer";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { ScrollContainerContext } from "../rye/RyeMainContentContainerBase";
import {
  RyeChartTooltip,
  RyeChartTooltipProvider,
} from "../rye/RyeChartTooltip";
import { RyeSegmentedButton } from "../rye/RyeSegmentedButton";
import { DollarAmount, useFormatDollarAmount } from "../common/DollarAmount";
import { RyeMainContentSplitPane } from "../rye/RyeMainContentSplitPane";
import { TransactionsDrilldownPane } from "../common/TransactionsDrilldownPane";
import type { TrendsReportState } from "./cashFlowState";
import { ReportDateRangeControl } from "./ReportDateRangeControl";
import { resolveDateRange } from "./reportDateRange";
import {
  TREND_SERIES,
  TrendSeries,
  TrendsData,
  TrendsMonth,
  useTrendsData,
} from "./useTrendsData";

// Hex values matching the Tailwind tokens in tailwind.config.js, and the same
// type colors as Money flow and Budgets. Nivo needs real colors, not class
// names.
const SERIES_STYLES: Record<TrendSeries, { label: string; color: string }> = {
  income: { label: "Income", color: "#3FD088" }, // green-500
  expenses: { label: "Expenses", color: "#0ea5e9" }, // blue-500 (sky-500)
  savings: { label: "Savings", color: "#7d57ff" }, // purple-500
};
// Appended to a color's hex to fade the bars that aren't selected.
const UNSELECTED_ALPHA_HEX = "59"; // ~35%

const AXIS_TEXT_COLOR = "#64748b"; // on-surface-500
const GRID_LINE_COLOR = "#e2e8f0"; // on-surface-200

const MIN_CHART_HEIGHT_PX = 260;
// The card's shared 18px padding above and below the chart.
const CHART_VERTICAL_PADDING_PX = 36;
// Roughly how many value gridlines to draw. The scale rounds to tidy amounts,
// so the exact count varies by a line or so.
const VALUE_TICK_COUNT = 4;
// Past this many months, the axis labels start skipping months.
const MAX_LABELED_MONTHS = 12;
const CHART_MARGIN = { top: 16, right: 16, bottom: 32, left: 64 };

const CHART_THEME = {
  text: {
    fontFamily: "InterVariable, sans-serif",
    fontSize: 11,
    fill: AXIS_TEXT_COLOR,
  },
  grid: { line: { stroke: GRID_LINE_COLOR, strokeWidth: 1 } },
  tooltip: {
    container: {
      background: "transparent",
      boxShadow: "none",
      padding: 0,
    },
  },
};

interface TrendsSelection {
  month: string;
  series: TrendSeries;
}

type TrendsBarDatum = { month: string } & Record<TrendSeries, number>;

function monthLabel(month: string): string {
  return dayjs(`${month}-01`).format("MMMM YYYY");
}

function isTrendSeries(value: unknown): value is TrendSeries {
  return TREND_SERIES.includes(value as TrendSeries);
}

/** Settings shared by the bar and line charts, so switching keeps the frame still. */
function useChartAxes(data: TrendsData) {
  const formatDollarAmount = useFormatDollarAmount();

  const tickStep = Math.ceil(data.months.length / MAX_LABELED_MONTHS);
  const tickValues = data.months
    .filter((_, index) => index % tickStep === 0)
    .map((month) => month.month);
  // The year is shown on the first tick and wherever a new year starts, so
  // every month can be placed without repeating the year under each one.
  const formatTick = (month: string) => {
    const date = dayjs(`${month}-01`);
    const showYear = month === data.months[0]?.month || date.month() === 0;
    return showYear ? date.format("MMM 'YY") : date.format("MMM");
  };

  // Anchored at zero, but extended below it for a month whose net went
  // negative, like a month of expenses where refunds outweighed purchases.
  // Left to itself, Nivo's bar chart keeps zero at the bottom and draws
  // negative bars over the axis.
  const valueMin = Math.min(
    0,
    ...data.months.flatMap((month) => Object.values(month.totals)),
  );

  return {
    valueMin,
    // Without this, the gridlines follow the scale's default of about ten
    // ticks, so they'd land between the labelled values as well as on them.
    gridYValues: VALUE_TICK_COUNT,
    axisLeft: {
      tickSize: 0,
      tickPadding: 8,
      tickValues: VALUE_TICK_COUNT,
      format: (value: string | number) =>
        formatDollarAmount(Number(value), {
          currencyCode: data.currency,
          showDollarSign: true,
          showMinus: true,
          compact: true,
        }),
    },
    axisBottom: {
      tickSize: 0,
      tickPadding: 10,
      tickValues,
      format: (value: string | number) => formatTick(String(value)),
    },
  };
}

function TrendsBarChart({
  data,
  monthsByKey,
  selection,
  onSelect,
}: {
  data: TrendsData;
  monthsByKey: Map<string, TrendsMonth>;
  selection: TrendsSelection | null;
  onSelect: (selection: TrendsSelection) => void;
}): JSX.Element {
  const { valueMin, ...axes } = useChartAxes(data);
  const barData = useMemo<TrendsBarDatum[]>(
    () => data.months.map(({ month, totals }) => ({ month, ...totals })),
    [data.months],
  );

  return (
    <ResponsiveBar<TrendsBarDatum>
      data={barData}
      keys={TREND_SERIES}
      indexBy="month"
      groupMode="grouped"
      valueScale={{ type: "linear", min: valueMin, max: "auto" }}
      margin={CHART_MARGIN}
      padding={0.25}
      innerPadding={2}
      borderRadius={2}
      theme={CHART_THEME}
      colors={(bar) => {
        const color = SERIES_STYLES[bar.id as TrendSeries].color;
        const isSelected =
          selection === null ||
          (selection.month === bar.indexValue && selection.series === bar.id);
        return isSelected ? color : `${color}${UNSELECTED_ALPHA_HEX}`;
      }}
      enableLabel={false}
      enableGridY={true}
      {...axes}
      onClick={(bar) => {
        if (isTrendSeries(bar.id)) {
          onSelect({ month: String(bar.indexValue), series: bar.id });
        }
      }}
      tooltip={(bar) => {
        const month = monthsByKey.get(String(bar.indexValue));
        return month && isTrendSeries(bar.id) ? (
          <TrendsTooltip
            month={month}
            hoveredSeries={bar.id}
            currency={data.currency}
          />
        ) : null;
      }}
      role="img"
      ariaLabel="Income, expenses, and savings by month"
    />
  );
}

function TrendsLineChart({
  data,
  monthsByKey,
  onSelect,
}: {
  data: TrendsData;
  monthsByKey: Map<string, TrendsMonth>;
  onSelect: (selection: TrendsSelection) => void;
}): JSX.Element {
  const { valueMin, ...axes } = useChartAxes(data);
  const lineData = useMemo(
    () =>
      TREND_SERIES.map((series) => ({
        id: series,
        data: data.months.map((month) => ({
          x: month.month,
          y: month.totals[series],
        })),
      })),
    [data.months],
  );

  return (
    <ResponsiveLine
      data={lineData}
      xScale={{ type: "point" }}
      yScale={{
        type: "linear",
        min: valueMin,
        max: "auto",
      }}
      margin={CHART_MARGIN}
      theme={CHART_THEME}
      colors={(series) => SERIES_STYLES[series.id as TrendSeries].color}
      lineWidth={2}
      pointSize={8}
      // The month in progress is hollow, so its partial totals don't read as
      // a drop.
      pointColor={({ point }) =>
        monthsByKey.get(String(point.data.x))?.inProgress
          ? "#ffffff"
          : point.seriesColor
      }
      pointBorderWidth={2}
      pointBorderColor={{ from: "seriesColor" }}
      enableGridX={false}
      enableGridY={true}
      {...axes}
      useMesh={true}
      onClick={(datum) => {
        if ("seriesId" in datum && isTrendSeries(datum.seriesId)) {
          onSelect({ month: String(datum.data.x), series: datum.seriesId });
        }
      }}
      tooltip={({ point }) => {
        const month = monthsByKey.get(String(point.data.x));
        return month && isTrendSeries(point.seriesId) ? (
          <TrendsTooltip
            month={month}
            hoveredSeries={point.seriesId}
            currency={data.currency}
          />
        ) : null;
      }}
      role="img"
      ariaLabel="Income, expenses, and savings by month"
    />
  );
}

/**
 * Rendered inside the card rather than alongside it, because it measures the
 * card's scroll viewport through ScrollContainerContext — which only the card's
 * descendants can see.
 */
function TrendsChart({
  data,
  chartType,
  selection,
  onSelect,
}: {
  data: TrendsData;
  chartType: TrendsReportState["chartType"];
  selection: TrendsSelection | null;
  onSelect: (selection: TrendsSelection) => void;
}): JSX.Element {
  // The chart has no intrinsic height, so it's sized to the card's visible
  // area. That area doesn't change as the chart inside it resizes, so there's
  // no feedback loop.
  const scrollContainer = useContext(ScrollContainerContext);
  const [availableHeightPx, setAvailableHeightPx] = useState(0);
  useEffect(() => {
    if (!scrollContainer) return;
    const measure = () =>
      setAvailableHeightPx(
        scrollContainer.clientHeight - CHART_VERTICAL_PADDING_PX,
      );
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scrollContainer);
    return () => observer.disconnect();
  }, [scrollContainer]);
  const chartHeightPx = Math.max(MIN_CHART_HEIGHT_PX, availableHeightPx);

  const monthsByKey = useMemo(
    () => new Map(data.months.map((month) => [month.month, month])),
    [data.months],
  );

  return (
    <RyeChartTooltipProvider
      style={{ height: `${chartHeightPx}px` }}
      className={buildClasses("[&_rect]:cursor-pointer")}
    >
      {chartType === "bars" ? (
        <TrendsBarChart
          data={data}
          monthsByKey={monthsByKey}
          selection={selection}
          onSelect={onSelect}
        />
      ) : (
        <TrendsLineChart
          data={data}
          monthsByKey={monthsByKey}
          onSelect={onSelect}
        />
      )}
    </RyeChartTooltipProvider>
  );
}

export function TrendsReport({
  state,
  setState,
}: {
  state: TrendsReportState;
  setState: (update: (prev: TrendsReportState) => TrendsReportState) => void;
}): JSX.Element {
  const { dateRange, chartType } = state;

  // Memoized so they keep a stable identity across renders — useTrendsData
  // keys its work off of them.
  const { beginDate, endDate } = useMemo(
    () => resolveDateRange(dateRange, dayjs()),
    [dateRange],
  );

  const data = useTrendsData(beginDate, endDate);
  const [selection, setSelection] = useState<TrendsSelection | null>(null);
  const selectedMonth = selection
    ? data.months.find((month) => month.month === selection.month)
    : undefined;

  useEffect(() => {
    setSelection(null);
  }, [beginDate, endDate]);

  return (
    <>
      <RyeMainContentHeader title="Historical trends" roundedTl={false}>
        <div
          className={buildClasses("flex", "items-center", "gap-3", "w-full")}
        >
          <ReportDateRangeControl
            presets={["year-to-date", "last-6-months", "last-12-months"]}
            range={dateRange}
            onChange={(update) =>
              setState((prev) => ({
                ...prev,
                dateRange: update(prev.dateRange),
              }))
            }
          />
          <div className={buildClasses("ml-auto", "shrink-0")}>
            <RyeSegmentedButton
              entries={[
                {
                  id: "bars",
                  icon: "bar_chart",
                  tooltip: "Bars",
                  onClick: () =>
                    setState((prev) => ({ ...prev, chartType: "bars" })),
                },
                {
                  id: "lines",
                  icon: "show_chart",
                  tooltip: "Lines",
                  onClick: () =>
                    setState((prev) => ({ ...prev, chartType: "lines" })),
                },
              ]}
              initEntryId={chartType}
            />
          </div>
        </div>
      </RyeMainContentHeader>
      <RyeMainContentSpacer direction="h" />
      <RyeMainContentSplitPane
        bottomPaneLabel="transactions pane"
        bottomPane={
          selection && selectedMonth ? (
            <TransactionsDrilldownPane
              onClose={() => setSelection(null)}
              key={`${selection.month}-${selection.series}`}
              groupLabel={`${SERIES_STYLES[selection.series].label} in ${monthLabel(selection.month)}`}
              transactions={selectedMonth.transactions[selection.series]}
            />
          ) : null
        }
      >
        <RyeMainContentCard
          roundedTl={false}
          roundedTr={false}
          roundedBl={false} // meets the left nav
          roundedBr={selection === null}
        >
          {data.hasData ? (
            <TrendsChart
              data={data}
              chartType={chartType}
              selection={selectedMonth ? selection : null}
              onSelect={setSelection}
            />
          ) : (
            <EmptyState />
          )}
        </RyeMainContentCard>
      </RyeMainContentSplitPane>
    </>
  );
}

function TrendsTooltip({
  month,
  hoveredSeries,
  currency,
}: {
  month: TrendsMonth;
  hoveredSeries: TrendSeries;
  currency: string;
}): JSX.Element {
  return (
    <RyeChartTooltip>
      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "gap-1.5",
          "bg-surface",
          "border",
          "border-surface-600",
          "shadow-glow",
          "rounded-md",
          "px-3",
          "py-2",
          "text-sm",
          "text-on-surface-700",
        )}
      >
        <span className={buildClasses("font-medium")}>
          {monthLabel(month.month)}
          {month.inProgress ? " so far" : ""}
        </span>
        <div
          className={buildClasses(
            "grid",
            "grid-cols-[auto_auto]",
            "items-center",
            "gap-x-4",
            "gap-y-0.5",
          )}
        >
          {TREND_SERIES.map((series) => (
            <div
              key={series}
              className={buildClasses("contents", {
                if: series !== hoveredSeries,
                then: "text-on-surface-500",
              })}
            >
              <span className={buildClasses("flex", "items-center", "gap-1.5")}>
                <span
                  className={buildClasses("w-2", "h-2", "rounded-sm")}
                  style={{ backgroundColor: SERIES_STYLES[series].color }}
                />
                {SERIES_STYLES[series].label}
              </span>
              <DollarAmount
                n={month.totals[series]}
                size="sm"
                showDollarSign={true}
                showMinus={month.totals[series] < 0}
                showPlus={false}
                align="right"
                currencyCode={currency}
              />
            </div>
          ))}
        </div>
        {month.unreviewedCount > 0 && (
          <span className={buildClasses("text-xs", "text-on-surface-500")}>
            Ignores {month.unreviewedCount.toLocaleString()} unreviewed{" "}
            {month.unreviewedCount === 1 ? "transaction" : "transactions"}
          </span>
        )}
      </div>
    </RyeChartTooltip>
  );
}

function EmptyState(): JSX.Element {
  return (
    <RyeNoResults
      icon="monitoring"
      title="No trends to show"
      description="There are no reviewed transactions in income, expense, or savings categories for this date range."
    />
  );
}
